import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import PannellumViewer from '../utils/360Viewer'; // Assuming this is your 360 viewer component
import {
  loadMainImage,
  loadAllThumbnails,
  getTourInfo,
  getRoomInfo,
  loadingSpinner,
  fetchWithRetry,
  getTourTypeHebrew,
} from '../utils/tourUtils';
import { FUNCTION_NAMES } from '../constants';
import { API_BASE_URL, LOGO_PATH } from '../config';
import { useViewerState } from './useViewerState';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from './ui/use-toast';
import { useNavigate } from 'react-router-dom';
import { useRoomImageCache } from './useRoomImageCache';
import { useMeasurements } from '../hooks/useMeasurements';
import MeasurementUI from './MeasurementUI';

const TourViewer = ({ mode = 'default', publicTourId = null, preselectedTour = null }) => {
  const [showDebug, setShowDebug] = useState(false);

  const [tours, setTours] = useState([]);
  const [selectedTour, setSelectedTour] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [currentRoomId, setCurrentRoomId] = useState(null);
  const [mediaPhoto, setMediaPhoto] = useState('');
  const [thumbnailUrls, setThumbnailUrls] = useState({});
  const [hotspots, setHotspots] = useState({}); // Ensure it's an empty object by default
  const [startPoints, setStartPoints] = useState({});
  const navigate = useNavigate(); // add inside AppContent
  const { roomImages, loadingRoomId, preloadImages, loadAndCacheImage } =
    useRoomImageCache();
  const { viewerState, pannellumRef, handleMouseDown, handleMouseUp } = useViewerState(
    startPoints,
    currentRoomId
  );
  const { toast } = useToast();

  // Measurement functionality
  const {
    measureMode,
    isProcessing,
    measurements,
    canMeasureCurrentRoom,
    toggleMeasurementMode,
    deleteMeasurement,
  } = useMeasurements(selectedTour, rooms, currentRoomId, pannellumRef);

  const handleTourSelect = async tour => {
    const { newRooms, hotspots, startPoints } = getTourInfo(tour);
    setSelectedTour(tour);
    setRooms(newRooms);
    setHotspots(hotspots);
    setStartPoints(startPoints);

    if (newRooms.length > 0) {
      const firstRoom = newRooms[0];
      // setFirstImageLoading(true);

      setCurrentRoomId(firstRoom.id);

      // ✅ Make sure it caches the first image
      const objectUrl = await loadAndCacheImage(tour, firstRoom.id);
      if (objectUrl) {
        setMediaPhoto(objectUrl);
      }

      // ✅ Now preload remaining images one-by-one
      preloadImages(tour, newRooms.slice(1));
      // setFirstImageLoading(false);
    }
  };

  const handleRoomChange = roomId => {
    debugger;
    setCurrentRoomId(roomId);
  };

  useEffect(() => {
    if (startPoints && Object.keys(startPoints).length > 0) {
      // Only update room if we’re at initial state
      setCurrentRoomId(prev => {
        if (prev === null || prev === 1) return 1;
        return prev;
      });
    }
  }, [startPoints]);

  useEffect(() => {
    const fetchAndSetPublicTour = async () => {
      if (mode === 'public' && publicTourId) {
        try {
          // Step 2: Find the tour by `id`
          const tourResponse = await fetchWithRetry({
            url: `${API_BASE_URL}/api/post`,
            data: {
              function_name: FUNCTION_NAMES.PRISMA,
              operation: 'tour.findUnique',
              payload: {
                id: publicTourId,
              },
            },
            label: 'fetch public tour',
          });

          const tour = tourResponse;
          // debugger;

          if (tour && tour.isActivePublic) {
            handleTourSelect(tour);
          } else {
            toast({
              title: 'not Tour',
              description: `no public tour.`,
              variant: 'destructive',
            });
            navigate('/', { state: {} });
            return;
          }
          toast({
            title: 'loaded!',
            description: 'public tour loaded',
          });
        } catch (err) {
          console.error(err);
          alert('Failed to load public tour');
        }
      }
    };

    fetchAndSetPublicTour();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, publicTourId]);
  useEffect(() => {
    const fetchTours = async () => {
      try {
        if (mode === 'public' || mode === 'embedded') {
          return;
        }
        // const response = await axios.get(`${API_BASE_URL}/api/get`, {
        //   params: {
        //     function_name: FUNCTION_NAMES.GET.GET_TOURS
        //   }
        // });
        // setTours(response.data);
        const response = await fetchWithRetry({
          url: `${API_BASE_URL}/api/post`,
          data: {
            function_name: FUNCTION_NAMES.PRISMA,
            operation: 'tour.findMany',
            payload: {},
          },
          label: 'tour findMany',
        });
        // debugger;
        setTours(response);
      } catch (error) {
        console.error('Error fetching tours:', error);
        alert('Error loading tours. Please try again.');
      }
    };
    fetchTours();
  }, [mode]);

  useEffect(() => {
    if (!selectedTour || !currentRoomId) return;

    const roomIdToLoad = currentRoomId; // 👈 capture the currentRoomId

    const cached = roomImages[roomIdToLoad];
    if (cached) {
      setMediaPhoto(cached);
    } else {
      setMediaPhoto(''); // optional: clear view
      loadAndCacheImage(selectedTour, roomIdToLoad).then(url => {
        if (url && currentRoomId === roomIdToLoad) {
          setMediaPhoto(url);
        }
      });
    }
  }, [currentRoomId, selectedTour, roomImages, loadAndCacheImage]);

  useEffect(() => {
    const updateThumbnails = async () => {
      if (selectedTour) {
        const urls = await loadAllThumbnails(selectedTour, rooms);
        setThumbnailUrls(urls);
      }
    };
    updateThumbnails();
  }, [selectedTour, rooms]);

  // Handle embedded mode with preselected tour
  useEffect(() => {
    if (mode === 'embedded' && preselectedTour && preselectedTour.id !== selectedTour?.id) {
      handleTourSelect(preselectedTour);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, preselectedTour?.id, selectedTour?.id]);

  useEffect(() => {
    if (!selectedTour || !currentRoomId) return;

    if (roomImages[currentRoomId]) {
      setMediaPhoto(roomImages[currentRoomId]);
    } else {
      loadMainImage(selectedTour, currentRoomId).then(setMediaPhoto);
    }
  }, [currentRoomId, roomImages, selectedTour]);
  const moveToNextRoom = () => {
    const index = rooms.findIndex(r => r.id === currentRoomId);
    if (index !== -1 && index < rooms.length - 1) {
      setCurrentRoomId(rooms[index + 1].id);
    }
  };

  const moveToPreviousRoom = () => {
    const index = rooms.findIndex(r => r.id === currentRoomId);
    if (index > 0) {
      setCurrentRoomId(rooms[index - 1].id);
    }
  };

  const currentRoomInfo = getRoomInfo(rooms, currentRoomId);

  return (
    <div dir="ltr" className="flex flex-col h-screen w-full">
      {/* 1. Header - Hidden in embedded mode */}
      {mode !== 'embedded' && (
        <div className="w-full p-4 bg-white border-b flex justify-between items-center">
        <div>
          <h2 className="text-lg font-bold">{selectedTour?.project?.name || 'Project Name'}</h2>
          <div className="text-sm text-gray-600">
            {getTourTypeHebrew(selectedTour?.tourType)}
          </div>{' '}
          {/* Show the tour type */}
          {/*<p className="text-sm text-gray-600">{selectedTour?.name || 'Tour Name'}</p>*/}
          <p className="text-sm text-gray-500">
            {/*Lot: {selectedTour?.lot?.name || '-'} | Building: {selectedTour?.building?.name || '-'}{' '}*/}
            {/*| Floor: {selectedTour?.floor?.name || '-'} | Space: {selectedTour?.space?.name || '-'}*/}
            {selectedTour?.lot?.name || '-'} | {selectedTour?.building?.name || '-'}{' '}
            {selectedTour?.floor?.name || '-'} | {selectedTour?.space?.name || '-'}
          </p>
        </div>
        <img src={LOGO_PATH} alt="Logo" className="h-10" />
        </div>
      )}

      {/* 2. Tour Selector */}
      {mode !== 'public' && mode !== 'embedded' && (
        <div className="w-full p-4 bg-gray-100 border-b">
          <Select
            value={selectedTour?.id || ''}
            onValueChange={value => {
              const tour = tours.find(t => t.id === value);
              if (tour) handleTourSelect(tour);
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="בחר סיור" />
            </SelectTrigger>
            <SelectContent>
              {tours.map(tour => (
                <SelectItem key={tour.id} value={tour.id}>
                  {tour.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {selectedTour && (
        <div className="flex flex-col flex-1">
          {/* Viewer */}
          <div className="relative flex-1">
            {loadingRoomId === currentRoomId && (
              <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/70">
                {loadingSpinner()}
              </div>
            )}
            <PannellumViewer
              pannellumRef={pannellumRef}
              mediaPhoto={mediaPhoto}
              pitch={viewerState.pitch}
              hotspotDebug={false}
              yaw={viewerState.yaw}
              hfov={viewerState.hfov}
              startPoints={startPoints}
              hotspots={hotspots}
              currentRoom={currentRoomId}
              currentRoomInfo={currentRoomInfo}
              rooms={rooms}
              thumbnailUrls={thumbnailUrls}
              handleNextRoom={moveToNextRoom}
              handlePreviousRoom={moveToPreviousRoom}
              handleRoomChange={handleRoomChange}
              handleMouseDown={handleMouseDown}
              handleMouseUp={handleMouseUp}
              canEdit={false}
              previewView={false}
              onViewUpdate={({ yaw, pitch }) => {
                // View update handled by useViewerState hook
              }}
            />
            
            {/* Measurement UI - Only show in non-public mode */}
            {mode !== 'public' && (
              <MeasurementUI
                measureMode={measureMode}
                isProcessing={isProcessing}
                measurements={measurements}
                currentRoomId={currentRoomId}
                canMeasure={canMeasureCurrentRoom()}
                onToggleMeasurement={toggleMeasurementMode}
                onDeleteMeasurement={deleteMeasurement}
                mode={mode}
              />
            )}
          </div>
          <div className="p-2">
            <Button variant="outline" onClick={() => setShowDebug(prev => !prev)} className="mb-2">
              {showDebug ? '➖ Hide Debug Info' : '➕ Show Debug Info'}
            </Button>

            {showDebug && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                {rooms.map(room => (
                  <div
                    key={room.id}
                    className={`border px-2 py-1 rounded text-center ${
                      roomImages[room.id]
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    <div className="font-bold">{room.name || `Room ${room.id}`}</div>
                    <div>{roomImages[room.id] ? '✅ Cached' : '⏳ Not cached'}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Thumbnails */}
          {/*<div className="w-full p-4 bg-gray-50 border-t">*/}
          {/*<div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">*/}
          {/*  {renderHotspotStartpointDiv(*/}
          {/*    rooms,*/}
          {/*    hotspots,*/}
          {/*    startPoints,*/}
          {/*    handleRoomChange,*/}
          {/*    thumbnailUrls,*/}
          {/*    currentRoom*/}
          {/*  )}*/}
          {/*</div>*/}
          {/*</div>*/}

          {/* Navigation */}
          {/*<div className="w-full p-4 bg-white flex gap-4 justify-center border-t">*/}
          {/*  <Button onClick={moveToPreviousRoom} disabled={currentRoom === 1}>*/}
          {/*    Previous Room*/}
          {/*  </Button>*/}
          {/*  <Button onClick={moveToNextRoom} disabled={currentRoom === rooms.length}>*/}
          {/*    Next Room*/}
          {/*  </Button>*/}
          {/*</div>*/}
        </div>
      )}
      {/* 5. Public Link Manager */}
      {/*{mode === 'default' && selectedTour && !isMobile && (*/}
      {/*  <div className="mt-8">*/}
      {/*    <PublicTourLinkManager tourId={selectedTour.id} />*/}
      {/*  </div>*/}
      {/*)}*/}
    </div>
  );
};

export default TourViewer;
